import { withMixpanel } from '@/contexts/analytics/mixpanel';

import { TARIFFS_VERSION_KEY, type TariffsVersion } from './version.constants';

/**
 * Adoption instrumentation for the tariffs old/new migration, mirroring the cash-position one
 * (`cash-position/context/versionTracking.ts`) so both migrations are read the same way in Mixpanel: a super-property
 * that rides EVERY event (answering "who is on which version") plus one event per switch, tagged with the surface that
 * caused it. `mixpanel` is only .init'd in production, so every call is wrapped.
 */

/** Where a switch came from — so we can tell a deliberate revert from a forced migration. */
export type TariffsVersionSource = 'settings_menu' | 'revert_bar' | 'deadline_forced';

const VERSION_PROPERTY = 'tariffs_version';
const VERSION_CHANGE_EVENT = 'tariffs_version_changed';

/** Reads the persisted version straight from localStorage (default NEW) — matches the context's initial read. */
export const getPersistedTariffsVersion = (): TariffsVersion => {
  if (typeof window === 'undefined') return 'NEW';
  try {
    return window.localStorage.getItem(TARIFFS_VERSION_KEY) === 'LEGACY' ? 'LEGACY' : 'NEW';
  } catch {
    return 'NEW';
  }
};

/** Registers the active version as a super-property (rides every event) + on the user profile. */
export const setTariffsVersionProperty = (version: TariffsVersion): void => {
  withMixpanel('setTariffsVersionProperty', (mixpanel) => {
    mixpanel.register({ [VERSION_PROPERTY]: version });
    if (typeof mixpanel.people?.set === 'function') {
      mixpanel.people.set({ [VERSION_PROPERTY]: version });
    }
  });
};

/** Updates the super-property and, when the value actually changed, fires the change event. */
export const recordTariffsVersionChange = (next: TariffsVersion, source: TariffsVersionSource): void => {
  const previous = getPersistedTariffsVersion();
  setTariffsVersionProperty(next);
  if (previous !== next) {
    withMixpanel('recordTariffsVersionChange', (mixpanel) => {
      mixpanel.track(VERSION_CHANGE_EVENT, { from: previous, to: next, source });
    });
  }
};
